import * as CognitoSDK from '@aws-sdk/client-cognito-identity-provider';
import { Auth } from 'aws-amplify';
import { AWS_API, AWS_CONFIGURATION } from 'config';
import { InitialLoginContextProps } from 'types/auth';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { authTokenStore } from './authTokenStore';

export const initialState: InitialLoginContextProps = {
    isLoggedIn: false,
    isInitialized: false,
    user: null
};

export const awsAdminClient = new CognitoSDK.CognitoIdentityProviderClient({
    region: AWS_CONFIGURATION.Auth.region,
    credentials: {
        accessKeyId: AWS_API.accessKeyId,
        secretAccessKey: AWS_API.secretAccessKey
    }
});

export const cleanLocalStorage = () => {
    localStorage.removeItem('shouldSkipSetupMFA');
    localStorage.removeItem('associateSoftwareToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('tenantSelected');
    localStorage.removeItem('tenantPath');
    localStorage.removeItem('tenant_id');
    authTokenStore.clearToken('idToken');
};

/**
 * Get temporary AWS credentials using AWS Cognito Identity Pool.
 * Exchanges the Cognito user pool ID token for temporary AWS credentials (auth token store or Amplify session).
 * @returns {Promise<{
 *   accessKeyId: string;
 *   secretAccessKey: string;
 *   sessionToken: string;
 *   expiration: Date;
 * }>}
 * @throws Error if there is an error fetching the temporary AWS credentials
 */
const resolveCognitoIdTokenForIdentityPool = async (): Promise<string> => {
    const cached = authTokenStore.getToken('idToken')?.trim();
    if (cached) return cached;

    try {
        const session = await Auth.currentSession();
        const idToken = session.getIdToken().getJwtToken();
        authTokenStore.setToken('idToken', idToken);
        return idToken;
    } catch {
        throw new Error('Unable to upload: your Cognito session is missing or expired. Please sign out and sign in again, then retry.');
    }
};

export const getTemporaryAWSCredentials = async () => {
    try {
        const idToken = await resolveCognitoIdTokenForIdentityPool();

        // Use AWS Cognito Identity Pool to exchange JWT token for temporary AWS credentials
        const credentialsProvider = fromCognitoIdentityPool({
            clientConfig: {
                region: AWS_API.region
            },
            identityPoolId: AWS_API.identityPoolId, // Replace with your Identity Pool ID or pass as parameter
            logins: {
                [`cognito-idp.${AWS_API.region}.amazonaws.com/${AWS_API.poolId}`]: idToken
            }
        });

        // Retrieve the credentials
        const credentials = await credentialsProvider();

        // Return credentials
        return {
            accessKeyId: credentials.accessKeyId,
            secretAccessKey: credentials.secretAccessKey,
            sessionToken: credentials.sessionToken,
            expiration: credentials.expiration
        };
    } catch (error) {
        console.error('Error fetching temporary AWS credentials:', error);
        throw error;
    }
};

export const setSession = (serviceToken?: string | null, idToken?: string | null) => {
    if (idToken) authTokenStore.setToken('idToken', idToken);
    else authTokenStore.clearToken('idToken');
};

export const AUTH_ROUTES = [
    '/login',
    '/register',
    '/forgot',
    '/recover',
    '/reset',
    '/user-confirmation',
    '/change',
    '/configured',
    '/setupmfa',
    '/select-tenant'
] as const;
